import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { UpdateJournalEntryInput } from 'app/api/API.service';
import { JournalAPIService } from 'app/api/journal.api.service';
import { AppState } from 'app/reducers';
import { parseErrorMessage } from 'app/utils/callstate';
import { sortBy } from 'lodash';
import { of } from 'rxjs';
import { catchError, map, mergeMap, withLatestFrom, switchMap } from 'rxjs/operators';
import {
    JournalActions,
    JournalActionTypes,
    JournalCancelled,
    JournalEntryUpdateCancelled,
    JournalEntryUpdated,
    JournalEntryUpdateRequested,
    JournalLoaded,
    JournalRequested
} from './journal.actions';
import { selectJournalCompanyId } from './journal.selectors';
import { CubeReloadRequested } from '../cube/cube.actions';

@Injectable()
export class JournalEffects {
    @Effect()
    loadJournal$ = this.actions$.pipe(
        ofType<JournalRequested>(JournalActionTypes.JournalRequested),
        switchMap(action => {
            // tslint:disable-next-line: no-shadowed-variable
            const { companyId, key, filter } = action.payload;
            return this.journalApiService.Journal(companyId, key, filter).pipe(
                map(
                    items =>
                        new JournalLoaded({
                            journal: sortBy(items, 'key')
                        })
                ),
                catchError(error => {
                    const errorMessage = parseErrorMessage(error);
                    return of(new JournalCancelled({ errorMessage }));
                })
            );
        })
    );

    @Effect()
    updateJournal$ = this.actions$.pipe(
        ofType<JournalEntryUpdateRequested>(
            JournalActionTypes.JournalEntryUpdateRequested
        ),
        withLatestFrom(this.store.pipe(select(selectJournalCompanyId))),
        mergeMap(([action, companyId]) => {
            const { key, name } = action.payload.entry;
            const input = { companyId, key, name } as UpdateJournalEntryInput;
            return this.journalApiService.UpdateJournalEntry(input).pipe(
                map(entry => new JournalEntryUpdated({ entry })),
                catchError(error => {
                    const errorMessage = parseErrorMessage(error);
                    return of(
                        new JournalEntryUpdateCancelled({ errorMessage })
                    );
                })
            );
        })
    );

    @Effect()
    updatedJournal$ = this.actions$.pipe(
        ofType<JournalEntryUpdated>(
            JournalActionTypes.JournalEntryUpdated
        ),
        switchMap(action => of(new CubeReloadRequested()))
    );

    constructor(
        private actions$: Actions<JournalActions>,
        private store: Store<AppState>,
        private journalApiService: JournalAPIService
    ) {}
}
